// Drive API — catalog product images (roadmap Phase 1.4 FE cutover).
//
// Real-engine (prod, USE_REAL_API) talks to the native /drive/* substrate:
//   ensure folder → presigned upload-url → browser PUT (same-origin /pexify-drive-prod/)
//   → confirm. Gallery reads the folder list (presigned download/thumbnail URLs).
// Maket mode (dev, MSW) keeps the existing /admin/catalog/.../drive-assets
// simulation so the dev catalog experience is unchanged. The gate is the
// codebase-wide USE_REAL_API toggle — one abstraction, two backends.

import { API_BASE, USE_REAL_API, apiUrl } from '../lib/api'
import type {
  DriveAsset as Asset,
  DriveAssetRole as AssetRole,
} from '../mocks/data/admin-catalog/drive-assets'

const CATALOG_COMPONENT = 'master_catalog'
const KNOWN_ROLES: AssetRole[] = [
  'cover',
  'detail',
  'variant',
  'mockup',
  'ai_lifestyle_mockup',
]

// Backend DriveFileRead (subset the gallery needs).
type DriveFileRead = {
  id: string
  role: string
  original_filename: string
  content_type: string
  file_size: number
  thumbnail_url: string | null
  download_url: string | null
  uploaded_at: string
  uploaded_by_user_id: string | null
}

function toAssetRole(role: string): AssetRole {
  return (KNOWN_ROLES as string[]).includes(role) ? (role as AssetRole) : 'detail'
}

function driveFileToAsset(f: DriveFileRead, productId: string): Asset {
  const url = f.download_url ?? f.thumbnail_url ?? ''
  return {
    id: f.id,
    product_id: productId,
    role: toAssetRole(f.role),
    filename: f.original_filename,
    mime_type: f.content_type,
    size_bytes: f.file_size,
    thumbnail_url: f.thumbnail_url ?? url,
    full_url: url,
    uploaded_by_user_id: f.uploaded_by_user_id ?? '',
    uploaded_at: f.uploaded_at,
  }
}

// ─── Real /drive/* helpers ──────────────────────────────────────────────────

// Shared three-step presigned upload (upload-url → PUT to storage → confirm);
// also used by drive-personal.ts (role 'document').
export async function uploadOneReal(
  folderId: string,
  file: File,
  role: string,
): Promise<void> {
  const contentType = file.type || 'application/octet-stream'
  const urlRes = await fetch(apiUrl('/drive/files/upload-url'), {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      folder_id: folderId,
      original_filename: file.name,
      content_type: contentType,
      file_size: file.size,
    }),
  })
  if (!urlRes.ok) throw new Error(`upload-url failed: ${urlRes.status}`)
  const { upload_url, provider_key } = (await urlRes.json()) as {
    upload_url: string
    provider_key: string
  }

  // Browser → object storage direct (same-origin /pexify-drive-prod/). The
  // presigned signature covers Content-Type — it MUST match the upload-url value.
  const putRes = await fetch(upload_url, {
    method: 'PUT',
    headers: { 'content-type': contentType },
    body: file,
  })
  if (!putRes.ok) throw new Error(`storage upload failed: ${putRes.status}`)

  const confRes = await fetch(apiUrl('/drive/files/confirm'), {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      folder_id: folderId,
      provider_key,
      original_filename: file.name,
      role,
    }),
  })
  if (!confRes.ok) {
    const err = await confRes.json().catch(() => null)
    throw new Error(err?.detail?.message ?? `confirm failed: ${confRes.status}`)
  }
}

// ─── Public API (USE_REAL_API-gated) ─────────────────────────────────────────

export async function fetchProductAssets(
  productId: string,
): Promise<{ assets: Asset[] }> {
  if (USE_REAL_API) {
    const res = await fetch(
      apiUrl(`/drive/folders/${CATALOG_COMPONENT}/${productId}`),
    )
    if (!res.ok) throw new Error(`fetch assets failed: ${res.status}`)
    const data = (await res.json()) as { items: DriveFileRead[] }
    return { assets: data.items.map((f) => driveFileToAsset(f, productId)) }
  }
  const res = await fetch(`${API_BASE}/admin/catalog/products/${productId}/drive-assets`)
  if (!res.ok) throw new Error(`fetch assets failed: ${res.status}`)
  return res.json()
}

export async function patchAssetRole(
  productId: string,
  assetId: string,
  role: AssetRole,
): Promise<unknown> {
  if (USE_REAL_API) {
    const res = await fetch(apiUrl(`/drive/files/${assetId}`), {
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ role }),
    })
    if (!res.ok) throw new Error(`role change ${res.status}`)
    return res.json()
  }
  const res = await fetch(
    `${API_BASE}/admin/catalog/products/${productId}/drive-assets/${assetId}`,
    {
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ role }),
    },
  )
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'failed' }))
    throw new Error(err.error ?? `role change ${res.status}`)
  }
  return res.json()
}

export async function deleteAsset(
  productId: string,
  assetId: string,
): Promise<unknown> {
  if (USE_REAL_API) {
    const res = await fetch(apiUrl(`/drive/files/${assetId}`), { method: 'DELETE' })
    if (!res.ok && res.status !== 204) throw new Error(`delete ${res.status}`)
    return null
  }
  const res = await fetch(
    `${API_BASE}/admin/catalog/products/${productId}/drive-assets/${assetId}`,
    { method: 'DELETE' },
  )
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'failed' }))
    throw new Error(err.error ?? `delete ${res.status}`)
  }
  return res.json()
}

export type UploadPayloadFile = {
  filename: string
  mime_type: string
  size_bytes: number
  role: AssetRole
  data_url?: string
  /** The real File handle — required for the real presigned PUT (maket ignores). */
  file?: File
}
